import React, { useEffect, useRef, useState } from 'react';
import { Loader2, MessageCircle, RefreshCw, Send, X } from 'lucide-react';

async function api(path: string, body?: unknown) {
  const response = await fetch(`/api${path}`, { method: body ? 'POST' : 'GET', headers: body ? { 'Content-Type': 'application/json' } : undefined, body: body ? JSON.stringify(body) : undefined, signal: AbortSignal.timeout(45000) });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Não foi possível concluir a operação.');
  return data;
}

export function modalKeys(event: React.KeyboardEvent<HTMLElement>, close: () => void, busy: boolean) {
  if (event.key === 'Escape' && !busy) { event.preventDefault(); close(); }
  if (event.key !== 'Tab') return;
  const items = Array.from(event.currentTarget.querySelectorAll<HTMLElement>('button:not(:disabled), a[href], input:not(:disabled), textarea:not(:disabled)'));
  if (!items.length) { event.preventDefault(); return; }
  const first = items[0]; const last = items[items.length - 1];
  if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
  else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
}

export function useWhatsApp() {
  const [available, setAvailable] = useState(false);
  const [state, setState] = useState('close');
  useEffect(() => {
    let alive = true;
    api('/health').then(data => { if (alive) setAvailable(data.whatsapp === true); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  useEffect(() => {
    if (!available) return;
    let alive = true;
    const refresh = () => api('/whatsapp/status').then(data => { if (alive) setState(data.state); }).catch(() => { if (alive) setState('unavailable'); });
    refresh(); const timer = window.setInterval(refresh, 10000);
    return () => { alive = false; window.clearInterval(timer); };
  }, [available]);
  return { available, state, setState };
}

export function WhatsAppConnection({ state, onState }: { state: string; onState: (state: string) => void }) {
  const [open, setOpen] = useState(false);
  const [qr, setQr] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const qrRef = useRef<HTMLButtonElement>(null);
  useEffect(() => { if (open) qrRef.current?.focus(); }, [open]);
  async function connect() {
    setBusy(true); setError(''); setQr('');
    try { const data = await api('/whatsapp/connect', {}); onState(data.state); setQr(data.qr || ''); if (!data.qr && data.state !== 'open') setError('O QR Code ainda não está pronto. Aguarde alguns segundos e atualize.'); }
    catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }
  return <>
    <button type="button" className="prospect-whatsapp-status" onClick={() => setOpen(true)}><MessageCircle size={16}/>{state === 'open' ? 'WhatsApp conectado' : 'Conectar WhatsApp'}</button>
    {open && <div className="prospect-modal-backdrop"><section role="dialog" aria-modal="true" aria-labelledby="whatsapp-title" className="prospect-modal" onKeyDown={e => modalKeys(e, () => setOpen(false), busy)}>
      <div className="prospect-modal-heading"><h2 id="whatsapp-title">WhatsApp</h2><button ref={qrRef} type="button" className="prospect-icon" onClick={() => setOpen(false)} aria-label="Fechar conexão"><X size={18}/></button></div>
      {state === 'open' ? <p className="prospect-connected">WhatsApp conectado à MainCode.</p> : <>
        {qr && <img className="prospect-qr" src={qr} alt="QR Code para conectar seu WhatsApp"/>}
        {error && <p role="alert" className="prospect-alert error">{error}</p>}
        <button type="button" className="prospect-primary" disabled={busy} onClick={connect}>{busy ? <Loader2 size={16} className="prospect-spin"/> : <RefreshCw size={16}/>} {qr ? 'Atualizar QR Code' : 'Gerar QR Code'}</button>
      </>}
    </section></div>}
  </>;
}

export interface SendPreview { phone: string; message: string; leadId: string; stage: number; name: string }
export function SendDialog({ preview, onClose, onSent }: { preview: SendPreview; onClose: () => void; onSent: (sentAt: string) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [locked, setLocked] = useState(false);
  const requestId = useRef(crypto.randomUUID());
  const cancelRef = useRef<HTMLButtonElement>(null);
  useEffect(() => { cancelRef.current?.focus(); }, []);
  async function send() {
    if (busy || locked) return;
    setBusy(true); setError('');
    try {
      const approval = await api('/messages/approve', { ...preview, requestId: requestId.current, approved: true });
      const result = approval.status === 'sent' ? approval : await api('/messages/send', { approvalId: approval.approvalId });
      if (result.status === 'sent') { onSent(result.sentAt); onClose(); }
      else { setLocked(true); setError(result.error || 'O envio está em processamento. Confira a conversa antes de aprovar outro envio.'); }
    } catch (e) { setError(`${(e as Error).message} Se houve perda de conexão durante o envio, confira a conversa. Tentar novamente nesta janela consulta a mesma aprovação.`); }
    finally { setBusy(false); }
  }
  return <div className="prospect-modal-backdrop"><section className="prospect-modal" role="dialog" aria-modal="true" aria-labelledby="send-title" onKeyDown={e => modalKeys(e, onClose, busy)}>
    <div className="prospect-modal-heading"><h2 id="send-title">Revisar envio</h2><button ref={cancelRef} type="button" className="prospect-icon" disabled={busy} onClick={onClose} aria-label="Cancelar envio"><X size={18}/></button></div>
    {preview.name && <strong>{preview.name}</strong>}<p className="prospect-send-phone">+{preview.phone}</p>
    <div className="prospect-send-preview">{preview.message}</div>
    {error && <p role="alert" className="prospect-alert error">{error}</p>}
    <div className="prospect-message-actions"><button type="button" className="prospect-secondary" disabled={busy} onClick={onClose}>Cancelar</button><button type="button" className="prospect-primary" disabled={busy || locked} onClick={send}>{busy ? <Loader2 size={17} className="prospect-spin"/> : <Send size={17}/>} {busy ? 'Enviando…' : 'Aprovar e enviar'}</button></div>
  </section></div>;
}
